import type { AnalyticsEventPayload } from '@atlaskit/analytics-next';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import { buildMessageStreamRequestContext } from '@atlassian/conversation-assistant-service/services/assistance-service/build-message-stream-request-context';
import type {
	ContentContext,
	useChatContextProvider,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { populateSendMessageContext } from './populateSendMessageContext';

type ChatContextStoreActions = ReturnType<typeof useChatContextProvider>;

export type BuildCanonicalRovoChatContextParams = {
	chatContextStoreActions: ChatContextStoreActions;
	productKey?: string;
	creationContextParams?: CreationContextParams;
	/** Defaults to active context from the store. */
	contentContext?: ContentContext;
	sendAnalyticsEvent?: (payload: AnalyticsEventPayload) => void;
};

/**
 * Builds the canonical Rovo Chat `sendMessageStream` context payload for the
 * current surface. Used by Enterprise HER pull-context so Voice and Chat share
 * one context contract.
 */
export async function buildCanonicalRovoChatContext({
	chatContextStoreActions,
	productKey = 'confluence',
	creationContextParams,
	contentContext,
	sendAnalyticsEvent = () => undefined,
}: BuildCanonicalRovoChatContextParams): Promise<Record<string, unknown>> {
	const contentContextValue =
		contentContext ?? chatContextStoreActions.getActiveContext() ?? 'global';
	const activeTargetId = chatContextStoreActions.getCurrentTargetId();

	const populated = await populateSendMessageContext(
		chatContextStoreActions,
		// Treat as a non-first prompt so staging-area / CwR follow-up context rules apply.
		false,
		// Source that is eligible for AIFC context requirements across CwR surfaces.
		'CONFLUENCE_CWR_BLANK_OBJECT',
		undefined,
		// Always include current page / surface context for Voice pull.
		true,
		contentContextValue,
		creationContextParams,
		productKey,
		sendAnalyticsEvent,
		activeTargetId,
	);

	const browserUrl = populated.browserContext?.context?.browserUrl ?? window.location.href;

	return buildMessageStreamRequestContext({
		browserUrl,
		// Voice CwR needs browsing context for placement and surface resolution.
		useCurrentBrowsingContext: true,
		editorContext: populated.editorContext,
		whiteboardContext: populated.whiteboardContext,
		slidesContext: populated.slidesContext,
		databaseContext: populated.databaseContext,
		dashboardEditorContext: populated.dashboardEditorContext,
		browserContext: populated.browserContext,
		additionalContext: populated.additionalContext,
		aiFeatureContext: populated.aiFeatureContext,
		jiraWorkflowContext: populated.jiraWorkflowContext,
		dashboardContext: populated.dashboardContext,
	});
}
